import {
  type FunctionComponent,
  type MouseEvent,
  useCallback,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { useAutoFocusRef } from '@trello/dom-hooks';
import { sendErrorEvent } from '@trello/error-reporting';
import { intl } from '@trello/i18n';
import { useWorkspaceId } from '@trello/id-context';
import { Button } from '@trello/nachos/button';
import { Textfield } from '@trello/nachos/textfield';
import type { TeamTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { useDeleteCollectionMutation } from './DeleteCollectionMutation.generated';
import { useEditCollectionMutation } from './EditCollectionMutation.generated';
import { useOrgTagsFragment } from './OrgTagsFragment.generated';
import { TagsDocument } from './TagsQuery.generated';

import styles from './EditCollection.less';

interface EditCollectionProps {
  tagId: string;
  onComplete: () => void;
}

export const EditCollection: FunctionComponent<EditCollectionProps> = ({
  onComplete,
  tagId,
}) => {
  const workspaceId = useWorkspaceId()!;
  const { data: organization } = useOrgTagsFragment({
    from: { id: workspaceId },
  });
  const existingTag = (organization?.tags ?? []).find(
    (tag) => tag.id === tagId,
  );
  const [name, setName] = useState<string>(existingTag?.name || '');
  const [tagIsDuplicate, setTagIsDuplicate] = useState(false);
  const inputElement = useRef<HTMLInputElement>(null);
  const [renameTag] = useEditCollectionMutation();
  const [deleteTag] = useDeleteCollectionMutation();

  const onEditTag = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      const source = 'workspaceBoardsScreen';
      const taskName = 'edit-tag';
      const traceId = Analytics.startTask({
        taskName,
        source,
      });
      try {
        await renameTag({
          variables: {
            idTag: tagId,
            idOrg: workspaceId,
            name,
            traceId,
          },
        });
        onComplete?.();
        Analytics.taskSucceeded({
          taskName,
          traceId,
          source,
        });
        Analytics.sendTrackEvent({
          action: 'edited',
          actionSubject: 'collection',
          source,
          attributes: {
            taskId: traceId,
            isBCFeature: true,
            requiredBC: true,
          },
          containers: {
            workspace: { id: workspaceId },
          },
        });
      } catch (err) {
        const tracedError = Analytics.taskFailed({
          taskName,
          traceId,
          source,
          error: err,
        });
        sendErrorEvent(tracedError || err, {
          tags: {
            ownershipArea: 'trello-web-eng',
            feature: 'Collections',
          },
        });
      }
    },
    [tagId, workspaceId, name, renameTag, onComplete],
  );

  /**
   * Delete the tag. This mutation does not return anything, so we
   * need to refetch the org tags to update the UI
   */
  const onDeleteTag = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      const source = 'workspaceBoardsScreen';
      const taskName = 'delete-tag';
      const traceId = Analytics.startTask({
        taskName,
        source,
      });
      try {
        await deleteTag({
          variables: {
            idTag: tagId,
            idOrg: workspaceId,
            traceId,
          },
          refetchQueries: [
            {
              query: TagsDocument,
              variables: { orgId: workspaceId },
              context: {
                operationName: 'DeleteCollectionMutation',
                document: TagsDocument,
              },
            },
          ],
        });
        onComplete?.();
        Analytics.sendTrackEvent({
          action: 'deleted',
          actionSubject: 'collection',
          source,
          attributes: {
            taskId: traceId,
            isBCFeature: true,
            requiredBC: true,
          },
          containers: {
            workspace: { id: workspaceId },
          },
        });
        Analytics.taskSucceeded({
          taskName,
          traceId,
          source,
        });
      } catch (err) {
        const tracedError = Analytics.taskFailed({
          taskName,
          traceId,
          source,
          error: err,
        });
        sendErrorEvent(tracedError || err, {
          tags: {
            ownershipArea: 'trello-web-eng',
            feature: 'Collections',
          },
        });
      }
    },
    [tagId, deleteTag, workspaceId, onComplete],
  );

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const updatedName = e.target.value.trim();
      setName(updatedName);
      if ((organization?.tags ?? []).find((tag) => tag.name === updatedName)) {
        setTagIsDuplicate(true);
      } else {
        setTagIsDuplicate(false);
      }
    },
    [setName, setTagIsDuplicate, organization?.tags],
  );

  useAutoFocusRef(inputElement, 1);

  return (
    <div>
      <Textfield
        label={intl.formatMessage({
          id: 'templates.org_filtering.name',
          defaultMessage: 'Name',
          description: 'Name field label',
        })}
        defaultValue={name}
        data-testid={getTestId<TeamTestIds>('collection-name-input')}
        onChange={onChange}
        ref={inputElement}
        isInvalid={tagIsDuplicate}
        aria-invalid={tagIsDuplicate}
      />
      {tagIsDuplicate && (
        <p className={styles.error}>
          <FormattedMessage
            id="alerts.there is already a collection with that name"
            defaultMessage="There is already a collection with that name"
            description="Duplicate collection name error message"
          />
        </p>
      )}
      <footer className={styles.footer}>
        <Button
          appearance="primary"
          data-testid={getTestId<TeamTestIds>('collection-name-save-button')}
          onClick={onEditTag}
          isDisabled={!name || tagIsDuplicate}
        >
          <FormattedMessage
            id="templates.org_filtering.save"
            defaultMessage="Save"
            description="Save button"
          />
        </Button>
        <Button
          appearance="danger"
          onClick={onDeleteTag}
          data-testid={getTestId<TeamTestIds>('collection-delete-button')}
        >
          <FormattedMessage
            id="templates.org_filtering.delete"
            defaultMessage="Delete"
            description="Delete button"
          />
        </Button>
      </footer>
    </div>
  );
};
